import { NgTemplateOutlet } from '@angular/common';
import { Component, computed, effect, inject, input, signal } from '@angular/core';
import { ConsultantActionHistoryModel } from '@lib/models/consultant/consultant-action-history.model';
import { ConsultantService } from '@lib/services/consultant/consultant.service';
import { OuiButton } from 'omnium-ui/button';
import { OuiIconModule } from 'omnium-ui/icon';
import { OuiLoading } from 'omnium-ui/loading';
import { OuiTabsModule } from 'omnium-ui/tabs';

const PAGE_SIZE = 20;
const DEFAULT_LABEL_COLOR = toCssColorVariable('on-surface--high-emphasis');

export interface ConsultantActivityActionDisplay {
  label: string;
  icon: string;
  iconColor: string;
  backgroundColor: string;
}

export interface ConsultantActivityDisplayItem {
  id: number;
  createdAt: Date;
  actorName: string;
  actionLabel: string;
  labelColor: string;
  habilitationLabel: string;
  formattedDateTime: string;
  icon: string;
  iconColor: string;
  backgroundColor: string;
}

export interface ConsultantActivityGroup {
  key: string;
  label: string;
  items: ConsultantActivityDisplayItem[];
}

export const ACTION_DISPLAY_BY_TYPE: Record<string, ConsultantActivityActionDisplay> = {
  HABILITATION_PARCOURS_INITIE_PAR_SHM: {
    label: "Parcours d'habilitation initié par le SHM pour",
    icon: 'add_circle',
    iconColor: toCssColorVariable('valid-color'),
    backgroundColor: toCssColorVariable('valid-pastel-color'),
  },
  HABILITATION_FORCE_ACTIVATED: {
    label: "Activation manuelle de l'habilitation",
    icon: 'lock_open',
    iconColor: toCssColorVariable('valid-color'),
    backgroundColor: toCssColorVariable('valid-pastel-color'),
  },
  HABILITATION_FORCE_DEACTIVATED: {
    label: "Désactivation manuelle de l'habilitation",
    icon: 'lock',
    iconColor: toCssColorVariable('surface-color'),
    backgroundColor: toCssColorVariable('secondary-200'),
  },
  HABILITATION_FORCE_CANCELLED: {
    label: "Arrêt de la désactivation manuelle de l'habilitation",
    icon: 'lock_open',
    iconColor: toCssColorVariable('alert-color'),
    backgroundColor: toCssColorVariable('alert-pastel-color'),
  },
  Gain: {
    label: "Activation de l'habilitation",
    icon: 'lock_open',
    iconColor: toCssColorVariable('valid-color'),
    backgroundColor: toCssColorVariable('valid-pastel-color'),
  },
  Perte: {
    label: "Désactivation de l'habilitation",
    icon: 'lock',
    iconColor: toCssColorVariable('surface-color'),
    backgroundColor: toCssColorVariable('secondary-200'),
  },
};

export const DEFAULT_ACTION_DISPLAY: ConsultantActivityActionDisplay = {
  label: 'Action réalisée',
  icon: 'history',
  iconColor: toCssColorVariable('on-surface--medium-emphasis'),
  backgroundColor: toCssColorVariable('background-color'),
};

export const HABILITATION_LABEL_BY_CODE: Record<string, string> = {
  IMMO: 'Immobilier',
  ASSU: 'Assurance',
  IOB: 'IOB',
  FINAN: 'Financement',
  CIF: 'CIF',
  ALPSI: 'ALPSI',
};

export function getActionDisplay(actionType: string): ConsultantActivityActionDisplay {
  return ACTION_DISPLAY_BY_TYPE[actionType] ?? DEFAULT_ACTION_DISPLAY;
}

export function getHabilitationLabel(habilitationCode?: string | null): string {
  return habilitationCode
    ? (HABILITATION_LABEL_BY_CODE[habilitationCode] ?? 'Habilitation non renseignée')
    : 'Habilitation non renseignée';
}

export function toActivityDisplayItem(
  item: ConsultantActionHistoryModel
): ConsultantActivityDisplayItem {
  const actionDisplay = getActionDisplay(item.actionType);
  const habilitationLabel = getHabilitationLabel(item.habilitationCode);

  return {
    id: item.id,
    createdAt: item.createdAt,
    actorName: getGestionnaireName(item),
    actionLabel: getActivityActionLabel(
      item.actionType,
      item.reason,
      actionDisplay,
      habilitationLabel
    ),
    labelColor: DEFAULT_LABEL_COLOR,
    habilitationLabel,
    formattedDateTime: formatDateTime(item.createdAt),
    icon: actionDisplay.icon,
    iconColor: actionDisplay.iconColor,
    backgroundColor: actionDisplay.backgroundColor,
  };
}

export function groupActivitiesByDate(
  items: ConsultantActivityDisplayItem[],
  now = new Date()
): ConsultantActivityGroup[] {
  const groups = new Map<string, ConsultantActivityDisplayItem[]>();

  for (const item of sortActivities(items)) {
    const key = getLocalDateKey(item.createdAt);
    groups.set(key, [...(groups.get(key) ?? []), item]);
  }

  return Array.from(groups.entries()).map(([key, groupItems]) => ({
    key,
    label: formatDateGroupLabel(groupItems[0].createdAt, now),
    items: groupItems,
  }));
}

export function groupActivitiesByHabilitation(
  items: ConsultantActivityDisplayItem[]
): ConsultantActivityGroup[] {
  const groups = new Map<string, ConsultantActivityDisplayItem[]>();

  for (const item of sortActivities(items)) {
    groups.set(item.habilitationLabel, [
      ...(groups.get(item.habilitationLabel) ?? []),
      item,
    ]);
  }

  return Array.from(groups.entries())
    .map(([label, groupItems]) => ({
      key: label,
      label,
      items: groupItems,
    }))
    .sort(
      (left, right) =>
        right.items[0].createdAt.getTime() - left.items[0].createdAt.getTime()
    );
}

function sortActivities(
  items: ConsultantActivityDisplayItem[]
): ConsultantActivityDisplayItem[] {
  return [...items].sort((left, right) => {
    const dateComparison = right.createdAt.getTime() - left.createdAt.getTime();

    return dateComparison === 0 ? right.id - left.id : dateComparison;
  });
}

function toCssColorVariable(colorVariableName: string): string {
  return `var(--${colorVariableName})`;
}

function getGestionnaireName(item: ConsultantActionHistoryModel): string {
  return item.gestionnaire
    ? `${item.gestionnaire.prenom} ${item.gestionnaire.nom}`.trim()
    : 'Gestionnaire non renseigné';
}

function getActivityActionLabel(
  actionType: string,
  reason: string | null,
  actionDisplay: ConsultantActivityActionDisplay,
  habilitationLabel: string
): string {
  if (!ACTION_DISPLAY_BY_TYPE[actionType]) {
    return `${actionDisplay.label} sur l'habilitation ${habilitationLabel}`;
  }

  const label = reason
    ? `${actionDisplay.label} ${habilitationLabel} - ${reason}`
    : `${actionDisplay.label} ${habilitationLabel}`;

  return actionType === 'HABILITATION_FORCE_ACTIVATED' ? `${label} pendant 24h` : label;
}

function getLocalDateKey(date: Date): string {
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');

  return `${year}-${month}-${day}`;
}

function formatDateGroupLabel(date: Date, now: Date): string {
  const formattedDate = new Intl.DateTimeFormat('fr-FR', {
    weekday: 'short',
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  }).format(date);

  return getLocalDateKey(date) === getLocalDateKey(now)
    ? `Aujourd'hui - ${formattedDate}`
    : formattedDate;
}

function formatDateTime(date: Date): string {
  return new Intl.DateTimeFormat('fr-FR', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
}

@Component({
  selector: 'lib-fiche-consultant-activite',
  standalone: true,
  imports: [NgTemplateOutlet, OuiTabsModule, OuiButton, OuiIconModule, OuiLoading],
  templateUrl: './fiche-consultant-activite.component.html',
  styleUrl: './fiche-consultant-activite.component.scss',
})
export class FicheConsultantActiviteComponent {
  private readonly consultantService = inject(ConsultantService);
  private requestId = 0;

  readonly codeConsultant = input.required<string>();
  readonly selectedTabIndex = signal<number>(0);
  readonly items = signal<ConsultantActionHistoryModel[]>([]);
  readonly totalCount = signal<number>(0);
  readonly hasNextPage = signal<boolean>(false);
  readonly isLoading = signal<boolean>(true);
  readonly isLoadingMore = signal<boolean>(false);
  readonly hasError = signal<boolean>(false);

  readonly displayItems = computed<ConsultantActivityDisplayItem[]>(() =>
    this.items().map(item => toActivityDisplayItem(item))
  );
  readonly dateGroups = computed<ConsultantActivityGroup[]>(() =>
    groupActivitiesByDate(this.displayItems())
  );
  readonly habilitationGroups = computed<ConsultantActivityGroup[]>(() =>
    groupActivitiesByHabilitation(this.displayItems())
  );

  constructor() {
    effect(() => {
      const codeConsultant = this.codeConsultant();
      void this.loadFirstPage(codeConsultant);
    });
  }

  async loadFirstPage(codeConsultant = this.codeConsultant()): Promise<void> {
    const currentRequestId = ++this.requestId;

    this.isLoading.set(true);
    this.hasError.set(false);
    this.items.set([]);
    this.totalCount.set(0);
    this.hasNextPage.set(false);

    try {
      const page = await this.consultantService.getConsultantActionHistory(
        codeConsultant,
        0,
        PAGE_SIZE
      );

      if (currentRequestId !== this.requestId) return;

      this.items.set(page.items);
      this.totalCount.set(page.totalCount);
      this.hasNextPage.set(page.hasNextPage);
    } catch (error) {
      console.error(error);
      if (currentRequestId === this.requestId) {
        this.hasError.set(true);
      }
    } finally {
      if (currentRequestId === this.requestId) {
        this.isLoading.set(false);
      }
    }
  }

  async loadMore(): Promise<void> {
    if (this.isLoadingMore() || !this.hasNextPage()) return;

    this.isLoadingMore.set(true);
    this.hasError.set(false);

    try {
      const page = await this.consultantService.getConsultantActionHistory(
        this.codeConsultant(),
        this.items().length,
        PAGE_SIZE
      );

      this.items.set([...this.items(), ...page.items]);
      this.totalCount.set(page.totalCount);
      this.hasNextPage.set(page.hasNextPage);
    } catch (error) {
      console.error(error);
      this.hasError.set(true);
    } finally {
      this.isLoadingMore.set(false);
    }
  }
}
